import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SLICE_NAMES } from '@/constants';
import { ITourPublished } from '@models/tours/ITourPublished';

export interface IToursState {
    tours: ITourPublished[];
    activeTourId: string | null;
    run: boolean;
    stepIndex: number;
    stepHistory: number[];
    isLoading: boolean;
}

const initialState: IToursState = {
    tours: [],
    activeTourId: null,
    run: false,
    stepIndex: 0,
    stepHistory: [],
    isLoading: false,
};

export const ToursSlice = createSlice({
    name: SLICE_NAMES.TOURS,
    initialState,
    reducers: {
        setTours: (state, action: PayloadAction<ITourPublished[]>) => {
            state.tours = action.payload;
        },
        startTour: (state, action: PayloadAction<string>) => {
            state.activeTourId = action.payload;
            state.run = true;
            state.stepIndex = 0;
            state.stepHistory = [];
        },
        restoreTour: (state, action: PayloadAction<{ tourId: string; stepIndex: number; stepHistory: number[] }>) => {
            state.activeTourId = action.payload.tourId;
            state.run = true;
            state.stepIndex = action.payload.stepIndex;
            state.stepHistory = action.payload.stepHistory;
        },
        stopTour: (state) => {
            state.activeTourId = null;
            state.run = false;
            state.stepIndex = 0;
            state.stepHistory = [];
        },
        setStepIndex: (state, action: PayloadAction<number>) => {
            state.stepIndex = action.payload;
        },
        setStepHistory: (state, action: PayloadAction<number[]>) => {
            state.stepHistory = action.payload;
        },
        pushStepHistory: (state, action: PayloadAction<number>) => {
            state.stepHistory.push(action.payload);
        },
        popStepHistory: (state) => {
            state.stepHistory.pop();
        },
        setRun: (state, action: PayloadAction<boolean>) => {
            state.run = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        markTourCompleted: (state, action: PayloadAction<string>) => {
            const tour = state.tours.find((t) => t.id === action.payload);
            if (tour) {
                tour.progress = {
                    tourId: tour.id,
                    status: 'completed',
                    lastStepIndex: tour.steps.length - 1,
                };
            }
            state.activeTourId = null;
            state.run = false;
            state.stepIndex = 0;
            state.stepHistory = [];
        },
        markTourSkipped: (state, action: PayloadAction<string>) => {
            const tour = state.tours.find((t) => t.id === action.payload);
            if (tour) {
                tour.progress = {
                    tourId: tour.id,
                    status: 'skipped',
                    lastStepIndex: state.stepIndex,
                };
            }
            state.activeTourId = null;
            state.run = false;
            state.stepIndex = 0;
            state.stepHistory = [];
        },
    },
});

export const {
    setTours,
    startTour,
    restoreTour,
    stopTour,
    setStepIndex,
    setStepHistory,
    pushStepHistory,
    popStepHistory,
    setRun,
    setLoading,
    markTourCompleted,
    markTourSkipped,
} = ToursSlice.actions;

export default ToursSlice.reducer;
